import { create } from 'zustand';
import { Subscription } from 'rxjs';
import { useMemo } from 'react';
import {
  MeetingConnectionState,
  MeetingRoomState,
} from '../../shared/interfaces/meeting-service.interface';

import { joinJitsiMeeting, leaveJitsiMeeting } from './utils/meeting-store-utils';
import { MeetingService } from '../../apps/meeting/services/MeetingService';
import { EmojiType } from 'nexgen-types';
import { jitsiService } from '../../shared/singletons/jitsi-singleton';
import { CONFERENCE_LEAVE_REASONS } from '../../utils/constants';
import { beginRoomSwitch, isRoomSwitchInFlightFor } from './utils/room-switch-guard';
import { useShallow } from 'zustand/react/shallow';
import { useWorkplaceStore } from './workplace.store';
import { useLiveCaptionsStore } from './liveCaptions.store';
import { applyCurrentRCStatus } from '../../apps/rocketchat/services/rocketChatPresenceSync';
import { trackCallHangUp, type CallTrigger } from '@host/utils/meeting-tracking';

export const SPEAKING_SORT_INTERVAL_MS = 2 * 60 * 1000;

// Temporarily disables reordering video tiles by speaking activity; join-order is used instead.
// Speaking detection/tracking still runs underneath so this can be flipped back on later.
const ENABLE_SPEAKER_REORDER = false;

// Delay (ms) before a speaking participant is promoted to the second tile slot.
const SPEAKER_PROMOTION_DELAY_MS = 2000;

// Module-level timer for speaker promotion — restarted each time a new speaker is detected.
let speakerPromotionTimer: ReturnType<typeof setTimeout> | null = null;

const clearSpeakerPromotionTimer = () => {
  if (!speakerPromotionTimer) return;
  clearTimeout(speakerPromotionTimer);
  speakerPromotionTimer = null;
};

const participantSpeakingDefaults = {
  participantJoinOrder: [] as string[],
  speakingTimeMs: {} as Record<string, number>,
  currentSpeakerId: null as string | null,
  previousSpeakerId: null as string | null,
  speakingSince: {} as Record<string, number>,
  activeSpeakerSortOrder: [] as string[],
  participantOrderRevision: 0,
  /** The participant currently promoted to tile slot 2 (set after SPEAKER_PROMOTION_DELAY_MS). */
  promotedSpeakerId: null as string | null,
};

// How long a user interaction (emoji) stays on before it clears itself. Hand raise is
// deliberately excluded (see HAND_EMOJI below): it must stay up until it is seen and
// manually lowered.
const EMOJI_AUTO_CLEAR_MS = 5000;

// The one emoji that never auto-clears.
const HAND_EMOJI: EmojiType = 'hand';

// Per-user auto-clear timers, keyed by userPid. Module-level (not store state) because they
// are pure scheduling — nothing renders off them.
const emojiAutoClearTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearEmojiAutoClearTimer = (userPid: string) => {
  const timer = emojiAutoClearTimers.get(userPid);
  if (!timer) return;
  clearTimeout(timer);
  emojiAutoClearTimers.delete(userPid);
};

const clearAllEmojiAutoClearTimers = () => {
  emojiAutoClearTimers.forEach((timer) => clearTimeout(timer));
  emojiAutoClearTimers.clear();
};

export const emojiIconMap = {
  hand: '✋',
  thumbs_up: '👍',
  thumbs_down: '👎',
  clap: '👏',
  laugh: '😂',
  thinking: '🤔',
  heart: '❤️',
  idea: '💡',
  celebrate: '🎉',
  shrug: '🤷',
  coffee: '☕',
  cant_hear_you: '🔇',
  bio_break: '🚽',
  happy: '😊',
  confused: '😕',
  surprised: '😲',
  time: '⏰',
  important: '⭐',
  hot: '🔥',
  music: '🎵',
};

type WhisperSessionStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | null;

/**
 * UI-facing status of the local user's own attempt to join a meeting. Kept separate from
 * the lower-level connectionState/meetingRoomState machine (which mirrors the Jitsi
 * conference and can churn during summon/room switches) so the meeting view can reliably
 * show a "connecting" tile and a retry affordance on failure.
 */
export type MeetingJoinStatus = 'idle' | 'connecting' | 'joined' | 'failed';

export interface Track {
  id: string;
  type: 'audio' | 'video' | 'screen';
  track: any;
  isMuted?: boolean;
}

export interface ParticipantTrack {
  id: string;
  displayName: string;
  isLocal: boolean;
  audioTrack?: Track;
  videoTrack?: Track;
  screenTrack?: Track;
  userPid?: string;
  avatarUrl: string;
  avatarThumbnailUrl?: string | null;
  connectionQuality?: number;
  /**
   * NGE-681: whether this participant holds the Jitsi moderator role. In an
   * auditorium this distinguishes presenters (moderators/speakers) — shown as
   * square stage tiles — from the listen-only audience (circular avatars).
   */
  isModerator?: boolean;
}

interface MeetingState {
  isScreenShareFullScreen: boolean;
  recordingSessionId: string | null;
  isRecording: boolean;
  connectionState?: MeetingConnectionState;
  meetingRoomState?: MeetingRoomState;
  /** UI-facing status of the local user's own join attempt (drives the connecting/retry tile). */
  meetingJoinStatus: MeetingJoinStatus;
  /** Last join request, replayed by retryJoinMeeting() when an initial join fails. */
  lastJoinRequest: { roomId: string; options?: any } | null;
  error: string | null;
  jitsiParticipants: Record<string, ParticipantTrack>;
  subscription: Subscription | null;
  currentRoomId?: string;
  currentRoomToken?: string;
  isMeetingViewExpanded: boolean;
  isMeetingHungUp: boolean;
  showControls: boolean;
  canShowDeviceSettingsPopup: boolean;
  canMinimizeMeetingPopup: boolean;
  meetingEmojis: Record<string, { emoji: EmojiType }>;
  dialogPosition: { x: number; y: number };
  meetingBackgrounds: any[];
  gridLayoutResetTrigger: number;
  whisperMeetingInfo?: any;
  whisperRoomParticipants?: Record<string, any>;
  whisperSessionStatus: WhisperSessionStatus;
  whisperRecipientId?: string;
  /** Unix ms timestamp set at CONFERENCE_JOINED; used to filter pre-join transcript segments. */
  localUserJoinedAt: number | null;
  /**
   * Unix ms timestamp of the most recent room join/switch this client started, or null.
   * Drives the participants grid's skeleton tiles (useParticipantSkeletonCells): the grid is
   * mounted/unmounted around joins by several views, so "are we joining?" can't live in
   * component state — a grid that mounts *after* the join would have no way to know one
   * just happened, and would render no placeholders at all.
   */
  roomJoinStartedAt: number | null;
  /** NGE-681: auditorium (broadcast) mode + the local user's role within it. */
  isAuditorium: boolean;
  auditoriumRole: 'moderator' | 'speaker' | 'audience' | null;
  /**
   * Whether this session is the local user's to start and end (its creator, or a
   * co-moderator named on it). Separate from `auditoriumRole`, which the backend
   * defaults to `moderator` for any member so they can present — so the role cannot
   * be used to decide who may end the session.
   *
   * `null` means the server did not say (it predates the flag); callers then fall
   * back to the role rather than locking the session's own creator out.
   */
  auditoriumCanModerate: boolean | null;
  /**
   * Whether the local user may moderate the room itself — mute, lock the chat or the
   * entry, promote and demote. Separate from both `auditoriumRole` (which says only
   * what they may publish: every member joins as `moderator` for that reason) and
   * `auditoriumCanModerate` (which is about owning the booking). They differ for a
   * promoted guest, who may run the room but not end the session.
   *
   * `null` means the server did not say — callers fall back to the role rather than
   * locking a real moderator out while the two sides are on different versions.
   */
  auditoriumCanManageRoom: boolean | null;
  auditoriumSessionPid: string | null;
  /** Room Chat (NGE-804): the session's Rocket.Chat public channel id, and whether it's currently locked. */
  auditoriumChatRoomId: string | null;
  auditoriumChatLocked: boolean;
  participantJoinOrder: string[];
  speakingTimeMs: Record<string, number>;
  currentSpeakerId: string | null;
  previousSpeakerId: string | null;
  /**
   * Everyone speaking right now, with when they started — unlike `currentSpeakerId`,
   * which keeps only the latest voice, this holds overlapping speakers too.
   */
  speakingSince: Record<string, number>;
  activeSpeakerSortOrder: string[];
  participantOrderRevision: number;
  promotedSpeakerId: string | null;

  setCurrentRoomToken: (token: string) => void;
  setAuditoriumMode: (
    isAuditorium: boolean,
    role?: 'moderator' | 'speaker' | 'audience' | null,
    sessionPid?: string | null,
    chatRoomId?: string | null,
    chatLocked?: boolean,
    canModerate?: boolean | null,
    canManageRoom?: boolean | null
  ) => void;
  setAuditoriumChatLocked: (chatLocked: boolean) => void;
  getCurrentRoomPid: () => string;
  setCurrentRoomId: (roomId: string, options?: any) => void;
  setIsRecording: (open: boolean) => void;
  setMeetingRoomState: (state: MeetingRoomState) => void;
  setMeetingJoinStatus: (status: MeetingJoinStatus) => void;
  retryJoinMeeting: () => Promise<void>;
  joinMeetingRoom: (roomPid: string, options?: any) => Promise<void>;
  leaveMeeting: (options?: { leaveReason?: string; trackingReason?: string }) => Promise<void>;
  setRecordingSessionId: (id: string | null) => void;
  setMeetingViewExpanded: (value: boolean) => void;
  setMeetingHungUpStatus: (value: boolean, trigger?: CallTrigger) => void;
  setIsScreenShareFullScreen: (show: boolean) => void;
  getAllParticipantIds: () => string[];
  setShowControls: (status: boolean) => void;
  setShowDeviceSettingsPopup: (status: boolean) => void;
  setMinimizeMeetingPopup: (status: boolean) => void;
  setDialogPosition: (x: number, y: number) => void;
  updateMeetingEmojiEntry: (userPid: string, emoji?: EmojiType) => void;
  setMeetingBackgrounds: (imagesList: any[]) => void;
  triggerGridLayoutReset: () => void;
  setWhisperMeetingInfo: (info: any) => void;
  setWhisperRoomParticipants: (participants: Record<string, any>) => void;
  setWhisperSessionStatus: (status: WhisperSessionStatus) => void;
  setWhisperRecipientId: (recipientId: string) => void;
  setLocalUserJoinedAt: (ts: number) => void;
  registerParticipantForOrder: (participantId: string) => void;
  unregisterParticipantForOrder: (participantId: string) => void;
  onParticipantSpeakingStarted: (participantId: string) => void;
  onParticipantSpeakingStopped: (participantId: string) => void;
  addParticipantSpeakingTime: (participantId: string, durationMs: number) => void;
  runParticipantPeriodicSort: () => void;
  resetParticipantSpeaking: () => void;
}

export const useMeetingStore = create<MeetingState>((set, get) => ({
  isRecording: false,
  isScreenShareFullScreen: false,
  showControls: true,
  recordingSessionId: null,
  error: null,
  jitsiParticipants: {},
  meetingEmojis: {},
  meetingBackgrounds: [],
  subscription: null,
  isMeetingViewExpanded: false,
  isMeetingHungUp: false,
  connectionState: undefined,
  meetingRoomState: undefined,
  meetingJoinStatus: 'idle',
  lastJoinRequest: null,
  canShowDeviceSettingsPopup: false,
  canMinimizeMeetingPopup: false,
  dialogPosition: { x: 0, y: 108 },
  gridLayoutResetTrigger: 0,
  whisperMeetingInfo: undefined,
  whisperRoomParticipants: undefined,
  whisperSessionStatus: null,
  whisperRecipientId: undefined,
  localUserJoinedAt: null,
  roomJoinStartedAt: null,
  isAuditorium: false,
  auditoriumRole: null,
  auditoriumCanModerate: null,
  auditoriumCanManageRoom: null,
  auditoriumSessionPid: null,
  auditoriumChatRoomId: null,
  auditoriumChatLocked: false,

  setAuditoriumMode: (
    isAuditorium,
    role = null,
    sessionPid = null,
    chatRoomId = null,
    chatLocked = false,
    canModerate = null,
    canManageRoom = null
  ) =>
    set(() => ({
      isAuditorium,
      auditoriumRole: role,
      auditoriumCanModerate: canModerate,
      auditoriumCanManageRoom: canManageRoom,
      auditoriumSessionPid: sessionPid,
      auditoriumChatRoomId: chatRoomId,
      auditoriumChatLocked: chatLocked,
    })),
  setAuditoriumChatLocked: (chatLocked) => set(() => ({ auditoriumChatLocked: chatLocked })),

  ...participantSpeakingDefaults,

  setIsRecording: (open: boolean) => set(() => ({ isRecording: open })),
  setIsScreenShareFullScreen: (show: boolean) => set(() => ({ isScreenShareFullScreen: show })),
  setCurrentRoomToken: (token: string) => set(() => ({ currentRoomToken: token })),
  joinMeetingRoom: (_, __) => joinJitsiMeeting(_, __),
  leaveMeeting: (options?: any) => leaveJitsiMeeting(options),
  setMeetingViewExpanded: (value: boolean) => set(() => ({ isMeetingViewExpanded: value })),
  setMeetingRoomState: (state) => set(() => ({ meetingRoomState: state })),
  setMeetingJoinStatus: (status) => set(() => ({ meetingJoinStatus: status })),
  retryJoinMeeting: async () => {
    const req = get().lastJoinRequest;
    if (!req?.roomId) return;
    set(() => ({ meetingJoinStatus: 'connecting' }));
    await joinJitsiMeeting(req.roomId, req.options);
  },
  setShowControls: (status) => set(() => ({ showControls: status })),
  getAllParticipantIds: () => Object.keys(get().jitsiParticipants),
  setRecordingSessionId: (id: string | null) => set(() => ({ recordingSessionId: id })),
  setMinimizeMeetingPopup: (status: boolean) => set(() => ({ canMinimizeMeetingPopup: status })),
  setShowDeviceSettingsPopup: (val: boolean) => set(() => ({ canShowDeviceSettingsPopup: val })),
  setMeetingBackgrounds: (imagesList: any[]) => set(() => ({ meetingBackgrounds: imagesList })),
  setWhisperSessionStatus: (status: WhisperSessionStatus) =>
    set(() => ({ whisperSessionStatus: status })),
  setWhisperRecipientId: (recipientId: string) => set(() => ({ whisperRecipientId: recipientId })),
  triggerGridLayoutReset: () =>
    set((state) => ({ gridLayoutResetTrigger: state.gridLayoutResetTrigger + 1 })),
  updateMeetingEmojiEntry: (userPid: string, emoji?: EmojiType) => {
    // Any new value (or a manual turn-off) supersedes a pending auto-clear for this user.
    clearEmojiAutoClearTimer(userPid);

    set((state) => {
      const updated = { ...state.meetingEmojis };

      if (!emoji) {
        delete updated[userPid];
      } else {
        updated[userPid] = { emoji };
      }

      return { meetingEmojis: updated };
    });

    // Every interaction except hand raise turns itself off after EMOJI_AUTO_CLEAR_MS. This
    // runs on the local click path and on the remote-event path alike (both land here), so
    // each client expires the entry on its own — no extra wiring in the UI components.
    if (!emoji || emoji === HAND_EMOJI) return;

    const timer = setTimeout(() => {
      emojiAutoClearTimers.delete(userPid);

      // Still the same emoji? A newer one would have cleared this timer already, but the
      // entry can also have been removed in the meantime.
      if (useMeetingStore.getState().meetingEmojis[userPid]?.emoji !== emoji) return;

      // Tell the room as well when it is our own interaction expiring. Every client runs
      // this same timer, but background tabs throttle timers, so the explicit off keeps
      // remote views from holding a stale emoji. The off handler is idempotent.
      if (useWorkplaceStore.getState().user?.pid === userPid) {
        MeetingService.eventRouter.emitEmojiOffEvent(emoji);
      }

      set((state) => {
        const updated = { ...state.meetingEmojis };
        delete updated[userPid];
        return { meetingEmojis: updated };
      });
    }, EMOJI_AUTO_CLEAR_MS);

    emojiAutoClearTimers.set(userPid, timer);
  },

  setCurrentRoomId: async (roomPid: string, options?: any) => {
    const existingRoomId = get().currentRoomId;
    // currentRoomId may be the full Jitsi id (…-meeting-<pid>), the short pid, or the
    // hung-up "meeting-<pid>" form. Treat any of them as "already on this room".
    const isSameRoom =
      !!existingRoomId &&
      (existingRoomId === roomPid || existingRoomId.endsWith(`meeting-${roomPid}`));

    // Idempotency guard: GuestWorkplacePage's effect can fire setCurrentRoomId twice for
    // the same room. Without this guard the second call runs the "leave first" branch
    // below and tears down the connection the first call is still establishing — causing a
    // 10s waitForConnection timeout before recovery. A repeat call for the current room is
    // a no-op (unless we're rejoining after an explicit hang-up).
    if (isSameRoom && !options?.skipMeetingJoin && !get().isMeetingHungUp) {
      return;
    }

    // Same idea for a switch that is still in flight: currentRoomId isn't updated until
    // after the leave below, so the guard above can't see the room we're already moving
    // into. Without this a double-click on a room ran a second full leave+join for it.
    if (!options?.skipMeetingJoin && isRoomSwitchInFlightFor(roomPid)) {
      return;
    }

    // Claims ownership of the conference for this switch. Every await below re-checks it, so
    // a switch that gets superseded mid-flight stops writing shared state instead of
    // clobbering the newer switch's room (see room-switch-guard.ts).
    const roomSwitch = beginRoomSwitch(roomPid);
    try {
      // Open the skeleton window BEFORE tearing the old conference down, not just before the
      // join. leaveMeeting() below clears jitsiParticipants and blanks meetingRoomState, and
      // it is not instant — opening the window after it left a gap where the grid had no
      // tiles, no skeletons, and (on the floor-map path, whose grid is mounted on
      // "joining or has participants") no grid mounted at all. That gap is the ~1s empty
      // state between the skeleton disappearing and the real cards arriving.
      if (!options?.skipMeetingJoin) {
        set(() => ({ roomJoinStartedAt: Date.now() }));
      }

      if (get().currentRoomId) {
        const leaveReason = options?.isBreakoutRoom
          ? CONFERENCE_LEAVE_REASONS.SWITCH_ROOM
          : undefined;
        if (!options?.skipMeetingJoin) {
          await get().leaveMeeting({ leaveReason, trackingReason: 'room_switch' });
        }
        // A newer switch started while we were leaving: it owns the teardown and the join
        // from here, and it has already left this conference. Anything we wrote past this
        // point would be for a room the user has moved on from.
        if (roomSwitch.isSuperseded()) return;
        // Drop the emoji entries and the pending auto-clear timers together: a timer left
        // running here would fire inside the next room and emit a stale emoji-off there.
        clearAllEmojiAutoClearTimers();
        set(() => ({ meetingEmojis: {}, meetingRoomState: undefined }));
        if (!options?.skipMeetingJoin) {
          MeetingService.eventRouter.emitMeetingEntryEvent(false);
          // Do NOT call applyCurrentRCStatus() here — it fires again immediately after
          // joinMeetingRoom() below, and both are fire-and-forget HTTP requests that can
          // arrive at the RC server out of order, leaving status stuck on the wrong value.
        }

        // Clear live captions when changing rooms
        const { clearTranscripts } = useLiveCaptionsStore.getState();
        clearTranscripts();
      }
      if (roomSwitch.isSuperseded()) return;
      // skipMeetingJoin: navigation that must not start a call (e.g. while on break).
      // Store in meeting-{roomPid} format so getCurrentRoomPid() still works for detecting
      // when someone enters this room (to restart the call via handleRoomChangeEvent).
      set(() => ({
        currentRoomId: options?.skipMeetingJoin ? `meeting-${roomPid}` : roomPid,
        isMeetingHungUp: options?.skipMeetingJoin ? true : false,
      }));
      if (roomPid && !options?.skipMeetingJoin) {
        // joinMeetingRoom → initLocalTracks reads mute state from localStorage
        await get().joinMeetingRoom(roomPid, options);
        // Don't announce presence in a room a newer switch has already moved us out of.
        if (roomSwitch.isSuperseded()) return;
        MeetingService.eventRouter.emitMeetingEntryEvent(true);
        applyCurrentRCStatus();
      }
    } finally {
      roomSwitch.finish();
    }
  },

  getCurrentRoomPid: (): string => {
    return get().currentRoomId?.match(/meeting-(.+)$/)?.[1] ?? '';
  },

  setMeetingHungUpStatus: async (isHungUp: boolean, trigger: CallTrigger = 'user') => {
    trackCallHangUp(isHungUp, trigger);
    if (isHungUp) {
      set(() => ({
        isMeetingHungUp: isHungUp,
        canMinimizeMeetingPopup: false,
        roomJoinStartedAt: null,
      }));
      jitsiService?.resetMuteStateToUnmuted();
      await get().leaveMeeting();
      applyCurrentRCStatus();
      // NGE-839/842: tell the backend the user left the meeting on every hang-up, so
      // the meeting never stays "open" in the metrics.
      MeetingService.eventRouter.emitMeetingEntryEvent(false);
      // End Call keeps the user in the room they are in — no redirect to the home room.
      // currentRoomId is left as-is, so getCurrentRoomPid() still resolves this room for
      // Start Call and for the auto-restart when someone enters (handleRoomChangeEvent).
    } else {
      const room_pid = get().getCurrentRoomPid();
      set(() => ({ isMeetingHungUp: isHungUp, currentRoomId: room_pid }));
      await get().joinMeetingRoom(room_pid);
    }
  },
  setDialogPosition: (x: number, y: number) => {
    set(() => ({ dialogPosition: { x, y } }));
  },
  setWhisperMeetingInfo: (info: any) => {
    set(() => ({ whisperMeetingInfo: info }));
  },
  setWhisperRoomParticipants: (participants: Record<string, any>) => {
    set(() => ({ whisperRoomParticipants: participants }));
  },
  setLocalUserJoinedAt: (ts: number) => set(() => ({ localUserJoinedAt: ts })),

  registerParticipantForOrder: (participantId) => {
    set((state) => {
      if (state.participantJoinOrder.includes(participantId)) return state;
      return {
        participantJoinOrder: [...state.participantJoinOrder, participantId],
        participantOrderRevision: state.participantOrderRevision + 1,
      };
    });
  },

  unregisterParticipantForOrder: (participantId) => {
    set((state) => {
      const { [participantId]: _removed, ...speakingTimeMs } = state.speakingTimeMs;
      const speakingSince = { ...state.speakingSince };
      delete speakingSince[participantId];
      if (state.currentSpeakerId === participantId) {
        clearSpeakerPromotionTimer();
      }
      return {
        speakingTimeMs,
        speakingSince,
        participantJoinOrder: state.participantJoinOrder.filter((id) => id !== participantId),
        activeSpeakerSortOrder: state.activeSpeakerSortOrder.filter((id) => id !== participantId),
        currentSpeakerId: state.currentSpeakerId === participantId ? null : state.currentSpeakerId,
        previousSpeakerId:
          state.previousSpeakerId === participantId ? null : state.previousSpeakerId,
        promotedSpeakerId:
          state.promotedSpeakerId === participantId ? null : state.promotedSpeakerId,
        participantOrderRevision: state.participantOrderRevision + 1,
      };
    });
  },

  onParticipantSpeakingStarted: (participantId) => {
    if (!(participantId in get().speakingSince)) {
      set((state) => ({
        speakingSince: { ...state.speakingSince, [participantId]: Date.now() },
      }));
    }

    // Duplicate audio-level event for the same active speaker — leave timer alone.
    if (get().currentSpeakerId === participantId) return;

    set((state) => ({
      currentSpeakerId: participantId,
      previousSpeakerId: state.currentSpeakerId,
    }));

    // Already holding the promoted tile — no order change and no new delay needed.
    if (get().promotedSpeakerId === participantId) {
      clearSpeakerPromotionTimer();
      return;
    }

    // Restart the promotion timer — only promote after continuous speech for the delay.
    clearSpeakerPromotionTimer();
    speakerPromotionTimer = setTimeout(() => {
      speakerPromotionTimer = null;
      set((state) => {
        if (state.currentSpeakerId !== participantId) {
          return state;
        }

        return {
          promotedSpeakerId: participantId,
          participantOrderRevision: state.participantOrderRevision + 1,
        };
      });
    }, SPEAKER_PROMOTION_DELAY_MS);
  },

  onParticipantSpeakingStopped: (participantId) => {
    if (participantId in get().speakingSince) {
      set((state) => {
        const speakingSince = { ...state.speakingSince };
        delete speakingSince[participantId];
        return { speakingSince };
      });
    }

    set((state) => {
      if (state.currentSpeakerId !== participantId) return state;
      clearSpeakerPromotionTimer();
      return { currentSpeakerId: null };
    });
  },

  addParticipantSpeakingTime: (participantId, durationMs) => {
    if (durationMs <= 0) return;
    set((state) => ({
      speakingTimeMs: {
        ...state.speakingTimeMs,
        [participantId]: (state.speakingTimeMs[participantId] ?? 0) + durationMs,
      },
    }));
  },

  runParticipantPeriodicSort: () => {
    const state = get();
    const activeIds = state.participantJoinOrder.filter(
      (id) => (state.speakingTimeMs[id] ?? 0) > 0
    );
    const activeSpeakerSortOrder = [...activeIds].sort(
      (a, b) => (state.speakingTimeMs[b] ?? 0) - (state.speakingTimeMs[a] ?? 0)
    );
    set({
      activeSpeakerSortOrder,
      participantOrderRevision: state.participantOrderRevision + 1,
    });
  },

  resetParticipantSpeaking: () => {
    clearSpeakerPromotionTimer();
    set(() => ({ ...participantSpeakingDefaults }));
  },
}));

export const getSelfUserPariticipant = (): ParticipantTrack | undefined => {
  const selfUserId = jitsiService?.getMyUserId();
  return useMeetingStore.getState().jitsiParticipants[selfUserId];
};

export const useActiveParticipantIds = (): string[] => {
  const jitsiParticipants = useMeetingStore((state) => state.jitsiParticipants);
  const orderState = useMeetingStore(
    useShallow((state) => ({
      joinOrder: state.participantJoinOrder,
      participantOrderRevision: state.participantOrderRevision,
      promotedSpeakerId: state.promotedSpeakerId,
    }))
  );

  return useMemo(() => {
    const participants = Object.values(jitsiParticipants);
    const baseIds: string[] = [];
    const localParticipantId = participants.find((participant) => participant.isLocal)?.id;

    for (const participant of participants) {
      if (participant.isLocal) {
        baseIds.push(participant.id);
      } else if (participant.userPid || participant.audioTrack || participant.videoTrack) {
        baseIds.push(participant.id);
      }
    }

    const joinIndex = (id: string) => {
      const index = orderState.joinOrder.indexOf(id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    baseIds.sort((a, b) => joinIndex(a) - joinIndex(b));

    const orderedIds = baseIds;

    if (!localParticipantId) {
      return baseIds;
    }

    const withoutLocal = orderedIds.filter((participantId) => participantId !== localParticipantId);
    if (withoutLocal.length === orderedIds.length) {
      return orderedIds;
    }

    // const withoutLocal = baseIds.filter((participantId) => participantId !== localParticipantId);
    // const { promotedSpeakerId } = orderState;

    // // Swap speaker into slot 2 only — do not insert+shift, or everyone else (e.g. B)
    // // would move and the grid remount would make those tiles flicker/resize.
    // // [you, A, B, C] + promote C → [you, C, B, A] not [you, C, A, B].
    // if (
    //   promotedSpeakerId &&
    //   promotedSpeakerId !== localParticipantId &&
    //   withoutLocal.includes(promotedSpeakerId)
    // ) {
    //   const ordered = [localParticipantId, ...withoutLocal];
    //   const speakerIndex = ordered.indexOf(promotedSpeakerId);
    //   if (speakerIndex > 1) {
    //     const previousSecond = ordered[1];
    //     ordered[1] = promotedSpeakerId;
    //     ordered[speakerIndex] = previousSecond;
    //   }
    //   return ordered;
    // }

    return [localParticipantId, ...withoutLocal];
  }, [jitsiParticipants, orderState]);
};
